import React, { useEffect, useMemo, useRef } from 'react';
import { Badge } from '@mui/material';
import moment from 'moment';
import {
  AlertsDrawer,
  FAIcon,
  IconStyle,
  useToast,
  Notification,
  DrawerDirections,
  useBreakpoint,
} from '../../components';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import { NotificationModel } from '../../../sdk/type';
import { readMessage, fetchNotification } from '../../../sdk/store/notificationActions';
import { toggleNotification } from '../../../sdk/store/headerActions';
import { AlertsType, TextLinkStyle } from '../../components/SummaryPane/SummaryPaneAlerts/SummaryPaneAlerts';
import styles from './NotificationDrawer.module.scss';

export const NotificationDrawer = () => {
  const dispatch = useAppDispatch();
  const { addToast } = useToast();
  const { messages, newMessages } = useAppSelector(state => state.notification);
  const { isNotificationOpen } = useAppSelector(state => state.header);
  const viewPortType = useBreakpoint();
  const isDesktopView = ['xs', 'sm', 'md'].indexOf(viewPortType) === -1;
  const drawerRef = useRef<HTMLDivElement>(null);

  const notificationTimer = useMemo(() => parseInt(process.env.REACT_APP_NOTIFICATION_TIMER ?? '300') * 1000, []);

  const notificationSchedule = () => {
    // Fetch for current run
    dispatch(fetchNotification());
    // Schedule for next run
    setTimeout(() => {
      notificationSchedule();
    }, notificationTimer);
  };

  useEffect(() => {
    // Scheduler start up
    notificationSchedule();
  }, []);

  useEffect(() => {
    if (newMessages && newMessages.length > 0) {
      // Display message using toast
      newMessages.forEach((msg: NotificationModel) => {
        if (!msg?.isToasted) {
          addToast({
            id: `notification_${msg.recId}`,
            content: <Notification message={`${msg.contentBody}`} />,
            topPosition: '20px',
          });
        }
      });
    }
  }, [newMessages]);

  const getAlertType = (alertType: string): AlertsType => {
    switch (alertType) {
      case 'ERROR':
        return AlertsType.ERROR;
      case 'WARNING':
        return AlertsType.WARNING;
      case 'ACTION':
        return AlertsType.ACTION;
      case 'INFO':
        return AlertsType.INFO;
      default:
        return AlertsType.INFO; // Default to INFO if alertType is not recognized
    }
  };

  const messageTemplate_info = (message: NotificationModel, formattedDate: string) => {
    return (
      <div className={styles['message-card']}>
        <div className={styles['message-card-content']}>{message.contentBody}</div>
        <div className={styles['message-card-read']} onClick={() => markMessageRead(message.recId)}>
          <FAIcon iconName="check" iconStyle={IconStyle.SOLID} className={`${styles['icon']} fa-lg`} />
        </div>
        <div className={styles['message-card-date']}>{formattedDate}</div>
      </div>
    );
  };

  const messageTemplate_action = (message: NotificationModel, formattedDate: string) => {
    return (
      <div className={styles['message-card']}>
        <div className={styles['message-card-content']}>{message.contentBody}</div>
        <div className={styles['message-card-read']} onClick={() => markMessageRead(message.recId)}>
          <FAIcon iconName="check" iconStyle={IconStyle.SOLID} className={`${styles['icon']} fa-lg`} />
        </div>
        <div className={styles['message-card-date']}>{formattedDate}</div>
      </div>
    );
  };

  const messageTemplate_warning = (message: NotificationModel, formattedDate: string) => {
    return (
      <div className={styles['message-card']}>
        <div className={styles['message-card-content']}>{message.contentBody}</div>
        <div className={styles['message-card-read']} onClick={() => markMessageRead(message.recId)}>
          <FAIcon iconName="check" iconStyle={IconStyle.SOLID} className={`${styles['icon']} fa-lg`} />
        </div>
        <div className={styles['message-card-date']}>{formattedDate}</div>
      </div>
    );
  };

  const messageTemplate_error = (message: NotificationModel, formattedDate: string) => {
    return (
      <div className={styles['message-card']}>
        <div className={styles['message-card-content']}>{message.contentBody}</div>
        <div className={styles['message-card-read']} onClick={() => markMessageRead(message.recId)}>
          <FAIcon iconName="check" iconStyle={IconStyle.SOLID} className={`${styles['icon']} fa-lg`} />
        </div>
        <div className={styles['message-card-date']}>{formattedDate}</div>
      </div>
    );
  };

  const messageTemplate = (message: NotificationModel) => {
    const formattedDate = moment(message.createdDate).format('DD/MM/YYYY h:mm A');
    // Customize messageTemplate_XXX for different type
    switch (message.alertType) {
      case 'INFO':
        return messageTemplate_info(message, formattedDate);
      case 'ACTION':
        return messageTemplate_action(message, formattedDate);
      case 'WARNING':
        return messageTemplate_warning(message, formattedDate);
      case 'ERROR':
        return messageTemplate_error(message, formattedDate);
      default:
        return messageTemplate_info(message, formattedDate);
    }
  };

  const alerts = messages.map(notification => ({
    alertType: getAlertType(notification.alertType),
    textLinkStyle: TextLinkStyle.UNDERLINE,
    content: messageTemplate(notification),
    formattedDate: moment(notification.createdDate).format('DD/MM/YYYY'),
  }));

  const markMessageRead = (recId: number) => {
    dispatch(readMessage(recId));
  };

  const toggleHandler = (flag = !isNotificationOpen) => {
    dispatch(toggleNotification(flag));
  };

  const handleClickOutside = (event: MouseEvent) => {
    if (drawerRef.current && !drawerRef.current.contains(event.target as Node)) {
      toggleHandler(false);
    }
  };

  useEffect(() => {
    if (isNotificationOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isNotificationOpen]);

  return (
    <div className={styles['framework-notification-drawer']} ref={drawerRef}>
      <div className={styles['notification-toggle']} onClick={() => toggleHandler()}>
        {isDesktopView && <div className={styles['title']}>Notification</div>}
        {!isDesktopView && (
          <FAIcon iconName="bell" iconStyle={IconStyle.REGULAR} className={`${styles['icon']} fa-lg`} />
        )}
        {messages.length > 0 && (
          <Badge className={styles['badge']}>{messages.length > 10 ? '10+' : messages.length}</Badge>
        )}
      </div>
      {isNotificationOpen && (
        <div className={styles['alerts-drawer-container']}>
          <AlertsDrawer
            header="Notification"
            onClose={() => toggleHandler(false)}
            alerts={alerts}
            includeBackdrop={true}
            customBackdrop={styles['alt-backdrop']}
            direction={isDesktopView ? DrawerDirections.LEFT : DrawerDirections.BOTTOM}
          />
        </div>
      )}
      {isNotificationOpen && !isDesktopView && (
        <div className={styles['modal-backdrop']} onClick={() => toggleHandler(false)} aria-hidden="true"></div>
      )}
    </div>
  );
};
